import ResponseHelper from './response-helper';

/**
 * Manages requests to webservice related to addresses.
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

export default (axios) => ({
  /**
   * @typedef Address
   * @type {Object}
   * @property {String} street
   * @property {String} district
   * @property {String} city
   * @property {String} state 2-chars state identifier
   * @property {String} country 2-chars country identifier
   */

  /**
   * Loads the address for given zip code.
   * @param {String} zipCode Zip code digits (00000000)
   * @returns {Promise<{code: Number, message: String, data: Address}>}
   */
  loadAddressFromZip(zipCode) {
    return axios({
      method: 'get',
      url: `locations/cep/${zipCode}`
    })
      .then(function (response) {
        const d = response.data;
        return Promise.resolve({
          data: {
            street: d.street,
            district: d.neighborhood,
            city: d.cityName,
            state: d.state,
            country: 'BR'
          },
          code: 0, // Code = 0 means user is logged in
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Loads states list for given country.
   * @param {String} countryCode 2-char country identifier
   * @returns {Promise<[{id: number, code: string, name: string}]>}
   */
  loadStatesFromCountry(countryCode) {
    return axios({
      method: 'get',
      url: `locations/state/country/${countryCode}`
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data,
          code: 0, // Code = 0 means user is logged in
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Loads cities list for given state.
   * @param {String} stateCode 2-char state identifier
   * @returns {Promise<[{id: number, code: string, name: string}]>}
   */
  loadCitiesFromState(stateCode) {
    return axios({
      method: 'get',
      url: `locations/city/state/${stateCode}`
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Loads cities list for given state.
   * @returns {Promise<[{alpha2Code: string, name: string, numericCode: string}]>}
   */
  loadCountries() {
    return axios({
      method: 'get',
      url: `locations/country`
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  }
});
